import { D, P } from "@angular/cdk/keycodes";
import { Component, ElementRef, Inject, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { AuthorizedUser, ConfigStorage, getFormErrorList, isObjectValidator, onValueChangeEvent, requiredIfValidator, requiredValidator, UserStorage } from "@scisys-uim/uim-common";
import { AssetsUser, Firearm, FIREARM_ASSIGNABLE_PERSON_METADATA, FIREARM_ASSIGNABLE_PERSON_TYPE, FIREARM_METADATA, FIREARM_TYPE, FirearmAssignablePerson, FirearmAssignment, FirearmAssignmentType, Ranger } from "assets-model";
import { FirearmAssignablePersonService, FirearmAssignmentCreationData, FirearmAssignmentService } from "assets-service";
import { BehaviorSubject, combineLatest, Observable, of, Subject, Subscription } from "rxjs";
import { filter, tap } from "rxjs/operators";



export interface FirearmAssignmentCreationDialogData {
  person?: FirearmAssignablePerson;
  firearm?: Firearm;
  comments?: string;
  firearmAssignment?: FirearmAssignment;
}

export interface FirearmAssignmentCreationDialogConfigData {
  ranger?: Ranger;
  person?: FirearmAssignablePerson;
  personSelectionListFilterLoad?: (text: string) => Observable<FirearmAssignablePerson[]>;
  firearm?: Firearm;
  firearmSelectionListFilterLoad?: (text: string) => Observable<Firearm[]>;
  firearmAssignmentType: FirearmAssignmentType;
  personalFirearmAssignment?: boolean;
}

@Component({
  selector: 'app-firearm-assignment-creation-dialog',
  templateUrl: './firearm-assignment-creation-dialog.component.html',
  styleUrls: ['./firearm-assignment-creation-dialog.component.scss']
})
export class FirearmAssignmentCreationDialogComponent implements OnInit {

  protected systemConfig: any;

  public form: FormGroup;

  public item: FirearmAssignmentCreationDialogData = {};

  protected userDetails: AuthorizedUser;

  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  private _onInitCompleteSubject: Subject<boolean> = new BehaviorSubject<boolean>(null);
  $onInitComplete: Observable<boolean> = this._onInitCompleteSubject.asObservable().pipe(filter(o => o != null));
  private onInitSubscription: Subscription = null;

  personDefaultDebounceMillis: number = 500;
  @ViewChild('person') personInput: ElementRef;
  private personSelectionListSubject: Subject<FirearmAssignablePerson[]> = new BehaviorSubject([]);
  personSelectionList: Observable<FirearmAssignablePerson[]> = this.personSelectionListSubject.asObservable();

  firearmDefaultDebounceMillis: number = 500;
  @ViewChild('firearm') firearmInput: ElementRef;
  private firearmSelectionListSubject: Subject<Firearm[]> = new BehaviorSubject([]);
  firearmSelectionList: Observable<Firearm[]> = this.firearmSelectionListSubject.asObservable();


  constructor(
    private dialogRef: MatDialogRef<FirearmAssignmentCreationDialogComponent, FirearmAssignmentCreationDialogData>,
    @Inject(MAT_DIALOG_DATA) public data: FirearmAssignmentCreationDialogConfigData,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private firearmAssignablePersonService: FirearmAssignablePersonService,
    private firearmAssigmentService: FirearmAssignmentService,
    protected configService: ConfigStorage,
    protected userStorage: UserStorage) {

    dialogRef.disableClose = true;

    this.data = this.data || {
      firearmAssignmentType: null
    };

    this.form = this.fb.group({
      firearm: [null, [requiredValidator, isObjectValidator,]],
      person: [null, [requiredValidator, isObjectValidator,]],
      comments: [null, [requiredValidator]],
    });

    this.item.person = this.data.person;
    this.item.firearm = this.data.firearm;

    if (this.data.ranger != null) {
      const rangerFilter = {
        sourceType: 'RANGER',
        sourceKey: this.data.ranger.rangerId,
        assignmentValid: true
      };
      this.firearmAssignablePersonService.findByFullFilters(rangerFilter).subscribe(result => {
        if (result?.items?.length == 1) {
          this.data.person = result.items[0];
          this.item.person = this.data.person;
          this.setDefaultDisabledFields();
        } else {
          this.personSelectionList = of(result?.items);
        }
      });
    }

    this.setDefaultDisabledFields();
  }

  protected onInitAsyncComplete() { }

  ngAfterViewInit() {
    this.wireFirearmAutocomplete();
    this.wirePersonAutocomplete();

    this.firearmSelectionListFilter(null);
    this.personSelectionListFilter(null);
  }

  setDefaultDisabledFields() {
    if (!this.form) {
      return;
    }
    if (this.data.firearm != null) {
      this.firearm.disable();
    }
    if (this.data.person != null) {
      this.person.disable();
    }
  }

  closeDialog() {
    this.dialogRef.close();
  }

  isAssignFirearmEnabled() {
    return !this.form.invalid && this.item?.firearm?.assetId != null && (this.item?.person != null);
  }

  assignFirearm() {
    this.firearmAssigmentService.createAssignment(<FirearmAssignmentCreationData>{
      assetId: this.item.firearm.assetId,
      person: {
        personId: this.item?.person?.personId,
        rangerId: this.item?.person?.ranger?.rangerId,
        username: this.item?.person?.user?.user
      },
      comments: this.item.comments,
      firearmAssignmentType: this.item.person.assignmentType,
      personalFirearmAssignment: this.data.personalFirearmAssignment,
    }).subscribe(firearmAssignment => {
      this.item.firearmAssignment = firearmAssignment;
      this.dialogRef.close(this.item);
    });
  }

  getFormErrorList() {
    var result = [...getFormErrorList(this.form)];
    return result;
  }

  // Firearm field

  wireFirearmAutocomplete(): void {
    if (this.firearm) {
      onValueChangeEvent(this.firearm, value => {
        if (value == "") {
          value = null;
          if (this.item) {
            this.item.firearm = null;
          }
        }
        if (value == null || typeof value == 'string') {
          this.firearmSelectionListFilter(value);
        }
      }, {
        debounceMillis: this.getFirearmLookupDebounceTime()
      });
    }
  }

  firearmSelectionListFilterLoad(text: string): Observable<Firearm[]> {
    if (!this.data.firearmSelectionListFilterLoad) {
      return of([]);
    }
    return this.data.firearmSelectionListFilterLoad(text);
  }

  getFirearmLookupDebounceTime(): number {
    if (this.systemConfig && this.systemConfig.firearm
      && this.systemConfig.firearm.lookup
      && this.systemConfig.firearm.lookup.debounceTime != null) {
      return this.systemConfig.firearm.lookup.debounceTime;
    }
    return this.firearmDefaultDebounceMillis;
  }
  getFirearmLookupMinCharacters(): number {
    if (this.systemConfig && this.systemConfig.firearm
      && this.systemConfig.firearm.lookup
      && this.systemConfig.firearm.lookup.minCharacters != null) {
      return this.systemConfig.firearm.lookup.minCharacters;
    }
    return 0;
  }

  firearmSelectionListFilter(text: string) {
    const minLookupChars = this.getFirearmLookupMinCharacters();
    if (minLookupChars > 0) {
      if (text == null || text.length < minLookupChars) {
        this.firearmSelectionListSubject.next([]);
        return;
      }
    }
    this.firearmSelectionListFilterLoad(text).subscribe(items => {
      this.firearmSelectionListSubject.next(items);
    });
  }


  firearmDisplay(item: Firearm) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    var description = FIREARM_TYPE.description(item);
    if (item.poolFirearm) {
      var poolSegment = " (Pool";
      if (item.currentAssignmentFirearmAssignmentType == 'ISSUE') {
        poolSegment = poolSegment + " - Issued)";
      } else if (item.currentAssignmentFirearmAssignmentType == 'STORAGE') {
        poolSegment = poolSegment + " - In Storage)";
      } else {
        poolSegment = poolSegment + ")"
      }
      description = description + poolSegment;
    }
    return description;
  }

  firearmChanging(value: Firearm, unsetDependents: boolean = true) { }

  firearmChanged(value: Firearm, unsetDependents: boolean = true) {
  }

  firearmCompare(a: any, b: any) {
    return FIREARM_METADATA.equals(a, b);
  }

  get firearm() {
    return this.control('firearm');
  }

  // Person field

  wirePersonAutocomplete(): void {
    if (this.person) {
      onValueChangeEvent(this.person, value => {
        if (value == "") {
          value = null;
          if (this.item) {
            this.item.person = null;
          }
        }
        if (value == null || typeof value == 'string') {
          this.personSelectionListFilter(value);
        }
      }, {
        debounceMillis: this.getPersonLookupDebounceTime()
      });
    }
  }

  personSelectionListFilterLoad(text: string): Observable<FirearmAssignablePerson[]> {
    if (!this.data.personSelectionListFilterLoad) {
      return of([]);
    }
    return this.data.personSelectionListFilterLoad(text);
  }

  getPersonLookupDebounceTime(): number {
    if (this.systemConfig?.firearmAssignablePerson?.lookup?.debounceTime != null) {
      return this.systemConfig.firearmAssignablePerson.lookup.debounceTime;
    }
    return this.personDefaultDebounceMillis;
  }

  getPersonLookupMinCharacters(): number {
    if (this.systemConfig?.firearmAssignablePerson?.lookup?.minCharacters != null) {
      return this.systemConfig.firearmAssignablePerson.lookup.minCharacters;
    }
    return 0;
  }

  personSelectionListFilter(text: string) {
    const minLookupChars = this.getPersonLookupMinCharacters();
    if (minLookupChars > 0) {
      if (text == null || text.length < minLookupChars) {
        this.personSelectionListSubject.next([]);
        return;
      }
    }
    this.personSelectionListFilterLoad(text).subscribe(items => {
      this.personSelectionListSubject.next(items);
    });
  }

  personDisplay(item: FirearmAssignablePerson) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return FIREARM_ASSIGNABLE_PERSON_TYPE.description(item);
  }

  personChanging(value: FirearmAssignablePerson, unsetDependents: boolean = true) { }

  personChanged(value: FirearmAssignablePerson, unsetDependents: boolean = true) {
  }

  personCompare(a: any, b: any) {
    return FIREARM_ASSIGNABLE_PERSON_METADATA.equals(a, b);
  }

  get person() {
    return this.control('person');
  }

  // Comments field
  commentsChanging(value: string, unsetDependents: boolean = true) { }

  commentsChanged(value: string, unsetDependents: boolean = true) {
  }

  get comments() {
    return this.control('comments');
  }

  // form controls general

  control(name: string) {
    return this.form ? this.form.get(name) : null;
  }

  // 'framework'

  ngOnInit() {
    this.onInitAsync();
  }


  protected onInitAsync() {
    const allObservables: Observable<any>[] = this.onInitAsyncObservables() || [];
    this.onInitSubscription = combineLatest(allObservables).subscribe(() => {
      this.onInitSubscription?.unsubscribe();
      this._onInitAsyncComplete();
    });
  }

  protected _onInitAsyncComplete() {
    this.onInitAsyncComplete();
    this.configService.itemChanges().subscribe((configData: any) => this.initConfig(configData));
    this._onInitCompleteSubject.next(true);
    this._onInitCompleteSubject.complete();
  }

  // These observables will be waited for, on page load, before the screen is ready to search.
  // These Observables are required by the framework - take care in overriding
  protected onInitAsyncObservables(): Observable<any>[] {
    return [this.onInitConfigObservable(),
    this.onInitUserDetails()];
  }
  protected onInitConfigObservable(): Observable<any> {
    return this.configService.resolve().pipe(tap((configData: any) => this.initConfig(configData)));
  }
  protected initConfig(config: any) {
    this.systemConfig = config || {};
  }
  protected onInitUserDetails(): Observable<AuthorizedUser> {
    return this.userStorage.nextNonNullItem().pipe(tap((userDetails: AuthorizedUser) => this.initUserDetails(userDetails)));
  }
  protected initUserDetails(userDetails: AuthorizedUser) {
    this.userDetails = userDetails;
  }


  disabledTooltip(element) {
    return element && (element.valid || !element.touched || !element.value);
  }
}